/**
 * hm-product-report.ts — zestawienia towarowe: stany, obroty, rotacja.
 *
 * Osiem raportów z menu „Zestawienia" kartoteki towarów. Wszystkie stoją na dwóch źródłach:
 * STANACH (jedno wywołanie WebAPI, per towar i magazyn) i OBROTACH liczonych z pozycji dokumentów
 * magazynowych w zakresie dat.
 *
 * ── DWA OGRANICZENIA, KTÓRE MUSZĄ BYĆ WIDOCZNE NA EKRANIE ───────────────────────────────────
 *  1. STAN NA DZIEŃ WSTECZ nie istnieje. Symfonia oddaje wyłącznie stan BIEŻĄCY
 *     (`InventoryStates`), bez historii. Raport „stany na dzień" pokazuje więc stan na teraz.
 *  2. ROTACJA liczy się normalnie do ŚREDNIEGO stanu w okresie, a średniego stanu nie mamy —
 *     liczymy do stanu bieżącego. Przy towarze, którego stan mocno się zmieniał, wynik będzie
 *     inny niż w Symfonii.
 * Obu nie da się obejść bez historii stanów, więc zamiast zgadywać — mówimy to wprost.
 */

import { fromMilli, toMilli } from './hm-report-core';

export const HM_PRODUCT_REPORTS = [
  'obroty-za-okres',
  'stany-na-dzien',
  'stany-i-obroty',
  'stany-i-rezerwacje',
  'ponizej-minimum',
  'powyzej-maksimum',
  'zalegajace',
  'rotacja',
] as const;

export type HmProductReportKind = (typeof HM_PRODUCT_REPORTS)[number];

/** Raporty, które potrzebują OBROTÓW, czyli pozycji dokumentów magazynowych (N+1). */
export const HM_PRODUCT_REPORTS_WITH_TURNOVER: readonly HmProductReportKind[] = [
  'obroty-za-okres',
  'stany-i-obroty',
  'zalegajace',
  'rotacja',
];

/** Raporty, które potrzebują progów min/max z KART towarów (osobne wywołanie na towar). */
export const HM_PRODUCT_REPORTS_WITH_LIMITS: readonly HmProductReportKind[] = [
  'ponizej-minimum',
  'powyzej-maksimum',
];

export function hmProductReportNeedsTurnover(kind: HmProductReportKind): boolean {
  return HM_PRODUCT_REPORTS_WITH_TURNOVER.includes(kind);
}

export function hmProductReportNeedsLimits(kind: HmProductReportKind): boolean {
  return HM_PRODUCT_REPORTS_WITH_LIMITS.includes(kind);
}

/** Stan jednego towaru w jednym magazynie. */
export interface HmProductStock {
  productId: number;
  warehouseId: number;
  warehouseName: string | null;
  /** Suma przyjęć i wydań — ile fizycznie leży w magazynie. */
  inStore: string;
  /** Stan handlowy: po odjęciu rezerwacji. */
  toSale: string;
}

/** Obrót towaru w zakresie dat, policzony z pozycji dokumentów magazynowych. */
export interface HmProductTurnover {
  productId: number;
  /** Ilość na dokumentach PRZYCHODOWYCH (PZ, PW, MM+). */
  income: string;
  /** Ilość na dokumentach ROZCHODOWYCH (WZ, RW, MM−). */
  outcome: string;
  documentCount: number;
}

export interface HmProductReportRow {
  productId: number;
  code: string;
  name: string;
  unit: string | null;
  typeLabel: string;
  active: boolean;

  /** Stan łączny ze wszystkich magazynów; `null` = towar nie ma wpisu stanu. */
  inStore: string | null;
  toSale: string | null;
  /** Różnica stanu magazynowego i handlowego, czyli ile jest zarezerwowane. */
  reserved: string | null;
  /** Rozbicie na magazyny — puste, gdy towar nigdzie nie leży. */
  stocks: HmProductStock[];

  /** Wypełnione tylko w raportach obrotowych. */
  income: string | null;
  outcome: string | null;
  turnoverDocuments: number | null;

  /** Progi z karty towaru — tylko w raportach o przekroczeniach. */
  minState: string | null;
  maxState: string | null;

  /**
   * Obrót rozchodowy podzielony przez stan BIEŻĄCY (nie średni — patrz nagłówek pliku).
   * `null`, gdy stan jest zerowy: dzielenie przez zero to nie „nieskończona rotacja".
   */
  rotation: string | null;
}

export interface HmProductReportParams {
  kind: HmProductReportKind;
  dateFrom: string | null;
  dateTo: string | null;
  /** Filtr magazynu po NAZWIE — stany przychodzą per magazyn. */
  warehouseName: string | null;
  /** Pokaż też towary bez stanu i bez obrotu (domyślnie odsiewane jako szum). */
  includeEmpty: boolean;
  ids: number[];
}

export interface HmProductReportResponse {
  rows: HmProductReportRow[];
  kind: HmProductReportKind;
  /**
   * Magazyny firmy — do wyboru w filtrze i do rozbicia stanów na kolumny. Bierzemy je ze
   * SŁOWNIKA, nie z wierszy: magazyn bez ani jednego towaru ma zostać na liście wyboru,
   * a nie zniknąć dlatego, że akurat jest pusty.
   */
  warehouses: string[];
  /** `true` = progi min/max są w wierszach. */
  limitsLoaded: boolean;
  /** `true` = obroty są w wierszach. */
  turnoverLoaded: boolean;
  /** Dokumenty, których pozycji nie udało się pobrać — obrót jest wtedy niepełny. */
  turnoverFailed: number[];
  /** `true` = zakres był za duży, żeby policzyć obroty; raport pokazuje same stany. */
  turnoverSkipped: boolean;
  fetchedAt: string;
  integrationName: string;
  params: HmProductReportParams;
}

/* ---------------------------------------------------------------- liczenie */

function sumMilli(values: (string | null)[]): string {
  return fromMilli(values.reduce<number>((acc, v) => acc + toMilli(v), 0));
}

/** Ilości z kilku magazynów w jedną liczbę — z tą samą skalą, co reszta modułu. */
export function totalStock(stocks: HmProductStock[]): { inStore: string; toSale: string } {
  return {
    inStore: sumMilli(stocks.map((s) => s.inStore)),
    toSale: sumMilli(stocks.map((s) => s.toSale)),
  };
}

export function reservedFrom(inStore: string, toSale: string): string {
  return fromMilli(toMilli(inStore) - toMilli(toSale));
}

/**
 * Rotacja: ile razy stan „obrócił się" w okresie. `null` przy zerowym stanie — wynik dzielenia
 * przez zero nie jest informacją, tylko dziurą po dzieleniu.
 */
export function rotationOf(outcome: string | null, inStore: string | null): string | null {
  const stock = toMilli(inStore);
  if (stock <= 0) return null;
  return (toMilli(outcome) / stock).toFixed(2);
}

/** Które wiersze zostają w danym raporcie — sedno różnicy między ośmioma pozycjami menu. */
export function filterProductReportRows(
  rows: HmProductReportRow[],
  params: HmProductReportParams,
): HmProductReportRow[] {
  const hasStock = (r: HmProductReportRow) => toMilli(r.inStore) !== 0;
  const hasTurnover = (r: HmProductReportRow) =>
    toMilli(r.income) !== 0 || toMilli(r.outcome) !== 0;

  return rows.filter((row) => {
    switch (params.kind) {
      case 'ponizej-minimum':
        // Próg zerowy znaczy „nie ustawiono", nie „minimum wynosi zero".
        return toMilli(row.minState) > 0 && toMilli(row.inStore) < toMilli(row.minState);
      case 'powyzej-maksimum':
        return toMilli(row.maxState) > 0 && toMilli(row.inStore) > toMilli(row.maxState);
      case 'zalegajace':
        // Zalega to, co LEŻY i się NIE RUSZAŁO w zakresie.
        return hasStock(row) && !hasTurnover(row);
      case 'rotacja':
        return row.rotation !== null;
      case 'obroty-za-okres':
        return params.includeEmpty || hasTurnover(row);
      case 'stany-na-dzien':
      case 'stany-i-rezerwacje':
        return params.includeEmpty || hasStock(row);
      case 'stany-i-obroty':
        return params.includeEmpty || hasStock(row) || hasTurnover(row);
    }
  });
}

/** Podsumowanie raportu towarowego — ilości, nie pieniądze. */
export interface HmProductReportTotals {
  products: number;
  inStore: string;
  toSale: string;
  reserved: string;
  income: string;
  outcome: string;
}

export function sumProductReport(rows: HmProductReportRow[]): HmProductReportTotals {
  return {
    products: rows.length,
    inStore: sumMilli(rows.map((r) => r.inStore)),
    toSale: sumMilli(rows.map((r) => r.toSale)),
    reserved: sumMilli(rows.map((r) => r.reserved)),
    income: sumMilli(rows.map((r) => r.income)),
    outcome: sumMilli(rows.map((r) => r.outcome)),
  };
}
